import type { ReactNode } from "preact/compat";
import { createContext } from "preact/compat";
import { useMemo, useContext, useState, useEffect } from "preact/hooks";
import type { Palette } from "@brickme/project-core/src/model/picture.ts";
import { transportPaletteToCore } from "@brickme/project-core/src/model/transport.ts";
import { useSelectedRegionApiClient } from "~/hooks/region/context.tsx";
import type {
	PaletteQuery,
	PaletteQueryVariables,
} from "./palette-query.api.generated.ts";
import paletteQuery from "./palette-query.api.graphql";

type ReferenceContextValue = {
	readonly systemPalette: Palette | undefined;
};

const ReferenceContext = createContext<ReferenceContextValue | undefined>(
	undefined,
);

type ReferenceProviderProps = {
	readonly children: ReactNode;
};

function ReferenceProvider({ children }: ReferenceProviderProps) {
	const [systemPalette, setSystemPalette] = useState<Palette | undefined>();
	const value = useMemo(
		() => ({
			systemPalette,
		}),
		[systemPalette],
	);
	const apiClient = useSelectedRegionApiClient();
	useEffect(() => {
		const abortController = new AbortController();
		(async () => {
			try {
				const { brickPalette } = await apiClient.query<
					PaletteQuery,
					PaletteQueryVariables
				>(paletteQuery, {}, { signal: abortController.signal });
				setSystemPalette(transportPaletteToCore(brickPalette));
			} catch (e) {
				// Abort expected
				if (abortController.signal.aborted) {
					return;
				}
				throw e;
			}
		})();
		return () => {
			abortController.abort("New API client");
		};
	}, [apiClient]);

	return (
		<ReferenceContext.Provider value={value}>
			{children}
		</ReferenceContext.Provider>
	);
}

function useSystemPalette() {
	const context = useContext(ReferenceContext);
	if (!context) {
		throw new Error("useSystemPalette must be used within a ReferenceProvider");
	}
	return context.systemPalette;
}

function useIsSystemPaletteLoaded() {
	const context = useContext(ReferenceContext);
	if (!context) {
		throw new Error(
			"useIsSystemPaletteLoaded must be used within a ReferenceProvider",
		);
	}
	return !!context.systemPalette;
}

export { ReferenceProvider, useIsSystemPaletteLoaded, useSystemPalette };
